import { useId, type ReactElement } from 'react';
import { renderTemplateText } from '@chatapp-inbox/shared';
import { Field, SelectField } from '../admin/Field';
import { CATEGORY_LABEL, optionLabel, templateKey, variableLabel, variableLimit } from './form';
import type { TemplatePickerState } from './use-template-picker';
import styles from './TemplatePicker.module.css';

export interface TemplatePickerProps {
  picker: TemplatePickerState;
  /** Enviando: os campos não aceitam mais digitação. */
  busy: boolean;
}

/**
 * Escolha do modelo, campos das variáveis e a prévia do que o cliente vai receber. Os modelos que o
 * app ainda não envia aparecem na lista, desabilitados e explicados (a pessoa sabe por que não dá).
 */
export function TemplatePicker({ picker, busy }: TemplatePickerProps): ReactElement {
  const previewId = useId();
  const { templates, selected } = picker;
  const unavailable = templates.filter((t) => !t.supported);
  const preview = selected ? renderTemplateText(selected, picker.values) : '';

  return (
    <div className={styles.picker}>
      <SelectField
        ref={(element) => picker.registerField('select', element)}
        label="Modelo de mensagem"
        hint="Só aparecem os modelos aprovados na Meta. Para criar ou editar um, use o Gerenciador do WhatsApp."
        value={picker.selectedKey}
        disabled={busy}
        error={picker.selectError}
        onChange={(event) => picker.select(event.target.value)}
      >
        <option value="">Escolha um modelo</option>
        {templates.map((t) => (
          <option key={templateKey(t)} value={templateKey(t)} disabled={!t.supported}>
            {optionLabel(t, templates)}
          </option>
        ))}
      </SelectField>

      {unavailable.length > 0 && (
        <details className={styles.unavailable}>
          <summary className={styles.summary}>
            {unavailable.length === 1
              ? '1 modelo indisponível (ver o motivo)'
              : `${unavailable.length} modelos indisponíveis (ver o motivo)`}
          </summary>
          <ul className={styles.reasons}>
            {unavailable.map((t) => (
              <li key={templateKey(t)}>
                <strong>{t.name}</strong>: {t.unsupportedReason}
              </li>
            ))}
          </ul>
        </details>
      )}

      {selected && (
        <>
          {selected.variables.map((v) => (
            <Field
              key={`${templateKey(selected)}:${v.key}`}
              ref={(element) => picker.registerField(v.key, element)}
              label={variableLabel(v)}
              hint={v.example ? `Exemplo: ${v.example}` : undefined}
              value={picker.values[v.key] ?? ''}
              error={picker.errors[v.key]}
              maxLength={variableLimit(v)}
              readOnly={busy}
              autoComplete="off"
              onChange={(event) => picker.setValue(v.key, event.target.value)}
            />
          ))}

          <section className={styles.preview} aria-labelledby={previewId}>
            <h3 id={previewId} className={styles.previewTitle}>
              Prévia da mensagem
            </h3>
            <p className={styles.bubble} dir="auto">
              {preview}
            </p>
            {selected.buttons.length > 0 && (
              <p className={styles.buttons}>
                Botões do modelo: {selected.buttons.map((b) => b.text).join(', ')}
              </p>
            )}
            <p className={styles.meta}>Categoria na Meta: {CATEGORY_LABEL[selected.category]}</p>
          </section>
        </>
      )}
    </div>
  );
}
